import React from "react";

import { faPhotoVideo } from "@fortawesome/free-solid-svg-icons";
import {
  faContainerStorage,
  faSort,
  faPrint,
  faHistory,
} from "@fortawesome/pro-regular-svg-icons";
import { useSelector } from "react-redux";

import { MultiButton } from "components/Button";
import { MultiButtonOption } from "components/Button/MultiButton/types";

import { AuditLogTypes } from "constants/auditLog";
import { Settings } from "constants/settings";

import {
  getCurrentSaleyardAuctionRoute,
  openAuditLogModal,
  openEditSaleLotModal,
} from "lib/navigation";

import {
  getCurrentSpeciesId,
  getHasWriteAccessInCurrentSale,
  getIsStaff,
  getSaleLotById,
  getSaleLotIdsByPenId,
  getSetting,
  getStatusByAuctionPenId,
} from "selectors";

import { useHasSaleLotPermission, useToggle } from "hooks";
import { SecondaryMultiButton } from "components/Button/MultiButton";
import { Species } from "constants/species";
import { AuctionPenStatus } from "constants/auctionPens";
import { SaleLotModalSection } from "constants/navigation";
import { PreventPropagation } from "components/Events";
import { ExportSite } from "constants/exportSites";
import { History } from "history";
import { useHistory } from "react-router-dom";
import RepenMultiple from "./RepenMultiple";
import ReorderPen from "./ReorderPen";

// A canonical list of all possible buttons that can appear in the pen actions menu
enum PenButtonOption {
  REPEN_ALL,
  REORDER_PEN,
  ADD_MEDIA,
  CATTLE_PEN_CARD,
  AUDIT_LOG,
}

interface ButtonContext {
  auctionPenId: string | null;
  history: History;
  saleLotId: string | null;
  sellPath: string;
  toggleReOrderPenModal: () => void;
  toggleRePenAllModal: () => void;
}

function getRepenAllButton({
  toggleRePenAllModal,
}: ButtonContext): MultiButtonOption {
  return {
    dataTour: "repenAll",
    title: "Re-Pen All",
    icon: faContainerStorage,
    onClick: toggleRePenAllModal,
  };
}

function getReorderPenButton({
  toggleReOrderPenModal,
}: ButtonContext): MultiButtonOption {
  return {
    dataTour: "reorderPen",
    title: "Re-Order Pen",
    icon: faSort,
    onClick: toggleReOrderPenModal,
  };
}

function getAddMediaButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    dataTour: "addMedia",
    title: "Add Media",
    icon: faPhotoVideo,
    onClick: () => openEditSaleLotModal(saleLotId, SaleLotModalSection.MEDIA),
  };
}

function getCattlePenCardButton({
  history,
  sellPath,
}: ButtonContext): MultiButtonOption {
  return {
    dataTour: "cattlePenCard",
    title: "Cattle Pen Card",
    icon: faPrint,
    onClick: () => history.push(`${sellPath}/cattlepencard`),
  };
}

function getAuditLogButton({ auctionPenId }: ButtonContext): MultiButtonOption {
  return {
    dataTour: "auditLog",
    title: "Audit Log",
    icon: faHistory,
    onClick: () => openAuditLogModal(AuditLogTypes.PEN, auctionPenId),
  };
}

const MultiButtonConfigMap = {
  [PenButtonOption.REPEN_ALL]: getRepenAllButton,
  [PenButtonOption.REORDER_PEN]: getReorderPenButton,
  [PenButtonOption.ADD_MEDIA]: getAddMediaButton,
  [PenButtonOption.CATTLE_PEN_CARD]: getCattlePenCardButton,
  [PenButtonOption.AUDIT_LOG]: getAuditLogButton,
};

// Determine which buttons should be shown based on the current state
// of the pen, user permissions, and sale type
function useGetPenButtonOptions(
  auctionPenId: string | null,
): PenButtonOption[] {
  const buttonOptions: PenButtonOption[] = [];

  const penStatus = useSelector(getStatusByAuctionPenId(auctionPenId));

  const hasChangeSaleLotPermission: boolean = useHasSaleLotPermission();
  const hasWriteAccess: boolean = useSelector<State, boolean>(
    getHasWriteAccessInCurrentSale,
  );

  const speciesId: number = useSelector<State, number>(getCurrentSpeciesId);

  const saleLotIds: string[] | null = useSelector<State, string[] | null>(
    getSaleLotIdsByPenId(auctionPenId),
  );

  const firstSaleLotId =
    Array.isArray(saleLotIds) && saleLotIds.length === 1 ? saleLotIds[0] : null;

  const firstSaleLot = useSelector(getSaleLotById(firstSaleLotId));

  const firstSaleLotIsListedWithAuctionsPlus =
    firstSaleLot &&
    firstSaleLot.exportSites &&
    firstSaleLot.exportSites.includes(ExportSite.AUCTIONS_PLUS);

  const showRePenAll: boolean =
    penStatus === AuctionPenStatus.NOT_SOLD && Boolean(saleLotIds);

  const hasPassedSale =
    penStatus === AuctionPenStatus.SOLD ||
    penStatus === AuctionPenStatus.DELIVERED;

  const showReOrderPen: boolean = !hasPassedSale;

  const isCattleSale: boolean = speciesId === Species.CATTLE;

  const isStaff: boolean = useSelector<State, boolean>(getIsStaff);

  if (!auctionPenId) {
    return buttonOptions;
  }

  if (hasWriteAccess && hasChangeSaleLotPermission) {
    if (showRePenAll) {
      buttonOptions.push(PenButtonOption.REPEN_ALL);
    }
    if (showReOrderPen) {
      buttonOptions.push(PenButtonOption.REORDER_PEN);
    }
  }
  if (firstSaleLotIsListedWithAuctionsPlus && !hasPassedSale) {
    buttonOptions.push(PenButtonOption.ADD_MEDIA);
  }
  if (isCattleSale && !hasPassedSale) {
    buttonOptions.push(PenButtonOption.CATTLE_PEN_CARD);
  }
  if (isStaff) {
    buttonOptions.push(PenButtonOption.AUDIT_LOG);
  }

  return buttonOptions;
}

interface ActionsComponentProps {
  auctionPenId: string;
}

function ActionsComponent(props: ActionsComponentProps) {
  const { auctionPenId } = props;

  const history: History = useHistory();

  const [repenIsOpen, toggleRePenAllModal] = useToggle(false);
  const [isReorderOpen, toggleReOrderPenModal] = useToggle(false);

  const buttonOptions: PenButtonOption[] = useGetPenButtonOptions(auctionPenId);

  const saleRoundId: string = useSelector(getSetting(Settings.round));

  const sellPath: string = getCurrentSaleyardAuctionRoute(
    saleRoundId,
    auctionPenId,
  );

  const firstSaleLotId: string | null = useSelector<State, string | null>(
    state => getSaleLotIdsByPenId(auctionPenId)(state)?.[0] || null,
  );

  const context: ButtonContext = {
    auctionPenId,
    history,
    saleLotId: firstSaleLotId,
    sellPath,
    toggleReOrderPenModal,
    toggleRePenAllModal,
  };

  const buttons = buttonOptions.map(option =>
    MultiButtonConfigMap[option](context),
  );

  return (
    <>
      <PreventPropagation>
        <MultiButton buttons={buttons} ButtonComponent={SecondaryMultiButton} />
      </PreventPropagation>
      {repenIsOpen && (
        <RepenMultiple
          auctionPenId={auctionPenId}
          toggleRepenIsOpen={toggleRePenAllModal}
        />
      )}
      {isReorderOpen && (
        <ReorderPen
          auctionPenId={auctionPenId}
          closeSelf={toggleReOrderPenModal}
        />
      )}
    </>
  );
}

export const Actions = React.memo(ActionsComponent);
